import {
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Text,
} from "@chakra-ui/react";
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useRef,
  useState,
} from "react";
import { AudioFile } from "../db";
import { AUTO_DELAY } from "./ActiveStudentPhrase";
import WaveformPlayback from "./WaveformPlayback";

export interface CountdownControls {
  startCountdown(): void;
  stopCountdown(): void;
}

interface CountdownProps {
  countdownSetting: number;
  audioFile?: AudioFile;
  callbackRef: MutableRefObject<CountdownControls | null>;
  onAudioPlay: () => void;
  audioElementRef: RefObject<HTMLAudioElement | null>;
  onDeleteRecording: () => void;
}

const Countdown = ({
  audioFile,
  countdownSetting,
  callbackRef,
  onAudioPlay,
  audioElementRef,
  onDeleteRecording,
}: CountdownProps) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const intervalRef = useRef<any>();

  callbackRef.current = {
    startCountdown() {
      let intervalCountdown = countdownSetting;
      if (intervalCountdown !== 0) {
        setIsRecording(true);
        handleCountdown(intervalCountdown);
      } else {
        setTimeout(() => {
          setIsRecording(true);
        }, AUTO_DELAY);
      }
    },
    stopCountdown() {
      clearInterval(intervalRef.current);
      setCountdown(0);
      setIsRecording(false);
    },
  };

  const handleCountdown = useCallback((intervalCountdown: number) => {
    setCountdown(intervalCountdown);
    intervalRef.current = setInterval(() => {
      if (intervalCountdown < 0) {
        clearInterval(intervalRef.current);
      }
      intervalCountdown = parseFloat((intervalCountdown - 0.1).toFixed(1));
      setCountdown(intervalCountdown);
    }, 100);
  }, []);

  return (
    <Flex
      position="absolute"
      width="100%"
      height="100%"
      top={0}
      bg="#EDF2F7"
      zIndex={1}
    >
      <Flex
        width="100%"
        position="relative"
        alignItems="center"
        justifyContent="center"
      >
        {isRecording ? (
          <CircularProgress
            min={0.01}
            max={countdownSetting}
            value={countdown === 0 ? 0.01 : countdown}
            size="90px"
            color="blue.500"
            sx={{
              circle: {
                transitionDuration: "0.1s",
                transitionTimingFunction: "linear",
              },
            }}
          >
            <CircularProgressLabel fontSize="4xl">
              {Math.ceil(countdown === 0 ? 0.01 : countdown)}
            </CircularProgressLabel>
          </CircularProgress>
        ) : (
          <Text fontSize="4xl" width="100%" textAlign="center">
            {countdown !== 0
              ? countdown
              : !audioFile && !isRecording
              ? "Ready to record"
              : ""}
          </Text>
        )}
      </Flex>
      {audioFile && !isRecording && (
        <WaveformPlayback
          audioFile={audioFile}
          onAudioPlay={onAudioPlay}
          audioElementRef={audioElementRef}
          onDeleteRecording={onDeleteRecording}
        />
      )}
    </Flex>
  );
};

export default Countdown;
